import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CafClusterListItem, CafCorpusEntry, entriesStateSupported, getCafClusterCount, listCafClusterQueue, listCafEntries,
} from '../../../api/cafCorpus';

const QUEUE_PAGE = 200;
const EXAMPLES_PAGE = 200;

export interface TeachData {
  queue: CafClusterListItem[];
  queueCursor: string | null;
  queueTotal: number | null;
  examples: CafCorpusEntry[];
  examplesNext: number | null;
  switchedOff: CafCorpusEntry[];
  /** null once every switched-off page has loaded. */
  switchedOffNext: number | null;
  offSupported: boolean;
  loading: boolean;
  problem: string;
  reload: () => Promise<void>;
  moreQueue: () => Promise<void>;
  moreExamples: () => Promise<void>;
  moreSwitchedOff: () => Promise<void>;
  dropFromQueue: (id: string, sender: string) => void;
}

const byId = <T extends { id: string }>(current: T[], next: T[]) => {
  const seen = new Set(current.map((item) => item.id));
  return [...current, ...next.filter((item) => !seen.has(item.id))];
};

const clusterKey = (id: string, sender: string) => JSON.stringify([id, sender]);

const byClusterIdentity = (current: CafClusterListItem[], next: CafClusterListItem[]) => {
  const seen = new Set(current.map((item) => clusterKey(item.id, item.sender)));
  return [...current, ...next.filter((item) => !seen.has(clusterKey(item.id, item.sender)))];
};

/** Controller ruling: a dropped id stays hidden only until the server catches up (a reload's own read no
 *  longer contains it) or 60s pass, whichever comes first -- after that, server truth wins even if it's
 *  still there. `now` defaults to `Date.now` and is injectable so the window is testable. */
const DROP_TTL_MS = 60_000;

export const useTeachData = (now: () => number = Date.now): TeachData => {
  const [queue, setQueue] = useState<CafClusterListItem[]>([]);
  const [queueCursor, setQueueCursor] = useState<string | null>(null);
  const [queueTotal, setQueueTotal] = useState<number | null>(null);
  const [examples, setExamples] = useState<CafCorpusEntry[]>([]);
  const [examplesNext, setExamplesNext] = useState<number | null>(null);
  const [switchedOff, setSwitchedOff] = useState<CafCorpusEntry[]>([]);
  const [switchedOffNext, setSwitchedOffNext] = useState<number | null>(null);
  const [offSupported, setOffSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const generation = useRef(0);
  // Groups just labelled from this tab: kept out of the queue even if a reload's own read is a beat
  // behind the write (Cosmos is eventually consistent) and still hands back the row. Maps
  // (id, sender) -> the time it was dropped, so another sender's same ID remains visible.
  const droppedAt = useRef<Map<string, number>>(new Map());

  const expireDrops = useCallback(() => {
    const cutoff = now() - DROP_TTL_MS;
    droppedAt.current.forEach((at, id) => { if (at <= cutoff) droppedAt.current.delete(id); });
  }, [now]);

  // For a plain "more" page: only the TTL can forget an id here -- a later page never containing an
  // earlier page's id says nothing about whether the server has caught up.
  const filterDropped = useCallback((items: CafClusterListItem[]) => {
    expireDrops();
    return items.filter((item) => !droppedAt.current.has(clusterKey(item.id, item.sender)));
  }, [expireDrops]);

  // For reload's own full page-1 read, which IS authoritative: once it stops containing a dropped id,
  // the server has caught up and there is nothing left to hide, so forget it immediately (not just at 60s).
  const reconcileDrops = useCallback((rawItems: CafClusterListItem[]) => {
    expireDrops();
    const rawKeys = new Set(rawItems.map((item) => clusterKey(item.id, item.sender)));
    droppedAt.current.forEach((_at, key) => { if (!rawKeys.has(key)) droppedAt.current.delete(key); });
    return rawItems.filter((item) => !droppedAt.current.has(clusterKey(item.id, item.sender)));
  }, [expireDrops]);

  const reload = useCallback(async () => {
    generation.current += 1;
    const mine = generation.current;
    setLoading(true);
    setProblem('');
    try {
      const [queuePage, total, onPage] = await Promise.all([
        listCafClusterQueue({ limit: QUEUE_PAGE }),
        Promise.resolve(getCafClusterCount('unlabelled')).catch(() => null),
        listCafEntries({ state: 'on', sizes: true, limit: EXAMPLES_PAGE, offset: 0 }),
      ]);
      const supported = entriesStateSupported(onPage, 'on');
      const offPage = supported
        ? await listCafEntries({ state: 'off', sizes: true, limit: EXAMPLES_PAGE, offset: 0 }).catch(() => null)
        : null;
      if (mine !== generation.current) return;
      setQueue(reconcileDrops(queuePage.items));
      setQueueCursor(queuePage.next_cursor);
      setQueueTotal(total);
      setExamples(onPage.items);
      setExamplesNext(onPage.next_offset);
      const offOk = Boolean(offPage && entriesStateSupported(offPage, 'off'));
      setOffSupported(offOk);
      if (supported && !offOk) setProblem("Couldn't load switched-off examples. Try again.");
      setSwitchedOff(offOk && offPage ? offPage.items : []);
      setSwitchedOffNext(offOk && offPage ? offPage.next_offset : null);
    } catch {
      if (mine === generation.current) setProblem("Couldn't load Teach the AI.");
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, [reconcileDrops]);

  useEffect(() => { void reload(); }, [reload]);

  const moreQueue = useCallback(async () => {
    if (!queueCursor) return;
    const mine = generation.current;
    try {
      const page = await listCafClusterQueue({ limit: QUEUE_PAGE, cursor: queueCursor });
      // A reload started (and possibly finished) while this page was in flight: its state is stale.
      if (mine !== generation.current) return;
      setQueue((prev) => byClusterIdentity(prev, filterDropped(page.items)));
      setQueueCursor(page.next_cursor);
    } catch {
      if (mine === generation.current) setProblem("Couldn't load more groups.");
    }
  }, [queueCursor, filterDropped]);

  const moreExamples = useCallback(async () => {
    if (examplesNext === null) return;
    const mine = generation.current;
    try {
      const page = await listCafEntries({ state: 'on', sizes: true, limit: EXAMPLES_PAGE, offset: examplesNext });
      if (mine !== generation.current) return;
      setExamples((prev) => byId(prev, page.items));
      setExamplesNext(page.next_offset);
    } catch {
      if (mine === generation.current) setProblem("Couldn't load more approved examples.");
    }
  }, [examplesNext]);

  const moreSwitchedOff = useCallback(async () => {
    if (switchedOffNext === null) return;
    const mine = generation.current;
    try {
      const page = await listCafEntries({ state: 'off', sizes: true, limit: EXAMPLES_PAGE, offset: switchedOffNext });
      if (mine !== generation.current) return;
      if (!entriesStateSupported(page, 'off')) throw new Error('Unsupported state');
      setSwitchedOff((prev) => byId(prev, page.items));
      setSwitchedOffNext(page.next_offset);
    } catch {
      if (mine === generation.current) setProblem("Couldn't load more switched-off examples.");
    }
  }, [switchedOffNext]);

  const dropFromQueue = useCallback((id: string, sender: string) => {
    droppedAt.current.set(clusterKey(id, sender), now());
    setQueue((prev) => prev.filter((group) => clusterKey(group.id, group.sender) !== clusterKey(id, sender)));
    setQueueTotal((prev) => (prev == null ? prev : Math.max(0, prev - 1)));
  }, [now]);

  return {
    queue, queueCursor, queueTotal, examples, examplesNext, switchedOff, switchedOffNext, offSupported, loading, problem,
    reload, moreQueue, moreExamples, moreSwitchedOff, dropFromQueue,
  };
};
